/**
 * Voice session controller — wraps ElevenLabs's Conversational AI client
 * SDK behind a small interface so the widget code stays SDK-agnostic.
 *
 * Why dynamic-import: the SDK ships ~150kB of audio plumbing (WebRTC,
 * Opus codec) that most chat-widget consumers never touch. We load it
 * lazily the first time the user clicks the mic button, so the widget's
 * baseline cold-load stays the size it is today.
 *
 * Vapi + Retell SDKs have their own dynamic-import drivers — see the
 * future vapi.driver.ts / retell.driver.ts. Only ElevenLabs is wired in
 * Fase 5; the others stub-throw with a clear message until Fase 6.
 */

export type VoiceProvider = 'elevenlabs' | 'vapi' | 'retell';

export type VoiceTurn = {
  role: 'user' | 'agent';
  text: string;
  /** When true the turn is still streaming and may grow on subsequent
   *  callbacks. False once the speaker stops talking. */
  final: boolean;
};

export type VoiceMode = 'idle' | 'connecting' | 'listening' | 'speaking' | 'ended';

export interface VoiceSessionHandle {
  /** End the session and tear down audio capture/playback. Idempotent. */
  end(): Promise<void>;
}

export interface VoiceSessionCallbacks {
  /** Fires for each transcript turn (user or agent). The widget pipes
   *  these into the chat message list so users see what was said. */
  onTurn?: (turn: VoiceTurn) => void;
  /** Fires when the agent's listening/speaking state changes. The
   *  widget uses this to render a "speaking" indicator. */
  onMode?: (mode: VoiceMode) => void;
  /** Fires when the session ends naturally (agent hung up, user
   *  closed the call, audio device disappeared). */
  onEnd?: (reason: string) => void;
  /** Fires for any unrecoverable error during the session. */
  onError?: (err: Error) => void;
}

export interface StartVoiceSessionOptions extends VoiceSessionCallbacks {
  provider: VoiceProvider;
  /** Provider-side agent id. Required when no signedUrl is provided
   *  (direct mode, ElevenLabs only). Optional when signedUrl is set —
   *  the backend already bound the session to the right agent. */
  agentId?: string;
  /** Pre-signed WebSocket URL minted by HW backend
   *  (`/api/voice-sessions/start`). When set, ElevenLabs uses it as the
   *  bootstrap and forces WebSocket transport — the only way to skip
   *  the LiveKit/WebRTC negotiation that times out against ElevenLabs's
   *  older LiveKit cluster. */
  signedUrl?: string;
  /**
   * Optional CDN override for the SDK module. esm.sh is the default —
   * users self-hosting under strict CSP can ship the SDK from their own
   * origin and pass the URL here.
   */
  sdkUrl?: string;
}

// Pinned to 1.4.0 — older versions (0.0.10) ship code that breaks
// when bundlers like Turbopack pre-bundle the SDK into the host app's
// chunk graph (manifested as "TypeError: e.x is not a function").
// 1.4.0 is the version @elevenlabs/react@1.3.x depends on so we know
// it works with the modern Conversation namespace.
const DEFAULT_ELEVENLABS_SDK_URL = 'https://esm.sh/@elevenlabs/client@1.4.0';

// Bundler-opaque dynamic import. Webpack/Turbopack/Vite peek inside
// `await import(url)` calls and try to pre-bundle external URLs at
// build time — for esm.sh modules this produces broken inline code
// or fails silently. Wrapping the call inside a `new Function()`
// body hides the import() from static analysis.
//
// CSP requirement: `unsafe-eval`. Most bundler-driven apps allow it
// by default. Strict-CSP customer embeds can override the loader by
// setting `globalThis.__hwVoiceImport = (url) => importFn(url)`
// BEFORE loading the widget.
const dynamicImport: (url: string) => Promise<unknown> = (() => {
  const override = (globalThis as { __hwVoiceImport?: (u: string) => Promise<unknown> })
    .__hwVoiceImport;
  if (typeof override === 'function') return override;
  try {
    return new Function('u', 'return import(u);') as (u: string) => Promise<unknown>;
  } catch {
    return (u: string) => import(u);
  }
})();

/**
 * Start a voice session against the configured agent. Resolves once
 * the WebSocket connection is up and the mic is capturing — errors
 * before that point reject the promise; errors during the session
 * fire onError instead.
 */
export async function startVoiceSession(
  opts: StartVoiceSessionOptions,
): Promise<VoiceSessionHandle> {
  if (opts.provider === 'elevenlabs') {
    return startElevenLabsSession(opts);
  }
  // Vapi/Retell drivers ship in a follow-up — stub-throwing now keeps
  // the option API forward-compatible without hiding the gap.
  throw new Error(
    `Voice provider "${opts.provider}" is not yet supported in the chat widget. Only "elevenlabs" works in Fase 5.`,
  );
}

// Subset of @elevenlabs/client@1.x types we actually consume.
type ElevenLabsDisconnect =
  | { reason: 'error'; message?: string; closeCode?: number; closeReason?: string }
  | { reason: 'agent'; closeCode?: number; closeReason?: string }
  | { reason: 'user' };

interface ElevenLabsMessagePayload {
  message: string;
  /** @deprecated — present in payload but `role` is the supported field. */
  source?: 'user' | 'ai';
  role?: 'user' | 'agent' | string;
  event_id?: number;
}

interface ElevenLabsSdk {
  Conversation: {
    startSession(params: {
      agentId?: string;
      /** Pre-signed WebSocket URL — forces WS transport and skips the
       *  LiveKit/WebRTC negotiation that fails against ElevenLabs's
       *  older LiveKit cluster (debugged 2026-05-02). */
      signedUrl?: string;
      onConnect?: (props: { conversationId: string }) => void;
      onDisconnect?: (details: ElevenLabsDisconnect) => void;
      onError?: (message: string, context?: unknown) => void;
      onMessage?: (msg: ElevenLabsMessagePayload) => void;
      onModeChange?: (info: { mode: 'speaking' | 'listening' }) => void;
    }): Promise<{ endSession(): Promise<void> }>;
  };
}

async function startElevenLabsSession(
  opts: StartVoiceSessionOptions,
): Promise<VoiceSessionHandle> {
  if (!opts.agentId && !opts.signedUrl) {
    throw new Error(
      'ElevenLabs session requires either `signedUrl` (HW-mediated, recommended) or `agentId` (direct public agents only).',
    );
  }

  const sdkUrl = opts.sdkUrl || DEFAULT_ELEVENLABS_SDK_URL;
  let sdk: ElevenLabsSdk;
  try {
    sdk = (await dynamicImport(sdkUrl)) as ElevenLabsSdk;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to load ElevenLabs SDK from ${sdkUrl}: ${msg}`);
  }

  if (!sdk?.Conversation?.startSession) {
    throw new Error(
      `Loaded ${sdkUrl} but it doesn't expose Conversation.startSession — pin the SDK URL via sdkUrl if you self-host.`,
    );
  }

  // Mic permission must be granted before the SDK opens its socket;
  // doing it explicitly gives us a clean rejection path if the user
  // blocks the prompt instead of letting the SDK throw a cryptic
  // "audio device busy" deep in its stack.
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // We don't need the stream object — we just needed the prompt.
    // Stop tracks immediately; the SDK reopens its own capture.
    stream.getTracks().forEach((t) => t.stop());
  } catch (err) {
    throw new Error(
      `Microphone permission denied: ${(err as Error).message}. Allow access in your browser to start a voice call.`,
    );
  }

  let lastUserText = '';
  let lastAgentText = '';

  // Prefer signedUrl when present — it's the only way to force the SDK
  // off the LiveKit/WebRTC path. With agentId alone, the SDK ignores
  // `connectionType` and still negotiates WebRTC, which times out
  // against ElevenLabs's older LiveKit cluster.
  const sessionParams: Parameters<ElevenLabsSdk['Conversation']['startSession']>[0] = {
    onConnect: () => opts.onMode?.('listening'),
    onDisconnect: (details) => {
      opts.onMode?.('ended');
      const reason =
        details.reason === 'error'
          ? details.message || details.closeReason || 'error'
          : details.reason;
      opts.onEnd?.(reason);
    },
    onError: (message, context) => {
      void context;
      opts.onError?.(new Error(message || 'Unknown ElevenLabs error'));
    },
    onMessage: (msg) => {
      const role: 'user' | 'agent' =
        msg.role === 'user' || msg.role === 'agent'
          ? msg.role
          : msg.source === 'user'
            ? 'user'
            : 'agent';
      const text = String(msg.message ?? '').trim();
      if (!text) return;
      const last = role === 'user' ? lastUserText : lastAgentText;
      if (text === last) return;
      if (role === 'user') lastUserText = text;
      else lastAgentText = text;
      opts.onTurn?.({ role, text, final: true });
    },
    onModeChange: (info) => opts.onMode?.(info.mode),
  };

  if (opts.signedUrl) sessionParams.signedUrl = opts.signedUrl;
  else if (opts.agentId) sessionParams.agentId = opts.agentId;

  const conv = await sdk.Conversation.startSession(sessionParams);

  return {
    end: () => conv.endSession(),
  };
}
